import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Field } from '../Field';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';

/** Alternate names for an ingredient — anyone can add one, admins can remove one. */
export function AliasManager({
  ingredientId,
  aliases,
  isAdmin,
}: {
  ingredientId: string;
  aliases: string[];
  isAdmin: boolean;
}) {
  const qc = useQueryClient();
  const [alias, setAlias] = useState('');
  const key = queryKeys.ingredientDetail(ingredientId);

  const add = useMutation({
    mutationFn: (input: string) => api.ingredients.addAlias(ingredientId, { alias: input }),
    onSuccess: () => {
      setAlias('');
      void qc.invalidateQueries({ queryKey: key });
    },
  });
  const remove = useMutation({
    mutationFn: (a: string) => api.ingredients.removeAlias(ingredientId, a),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = alias.trim();
    if (!trimmed) return;
    add.mutate(trimmed);
  }

  return (
    <div className="flex flex-col gap-3">
      {aliases.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {aliases.map((a) => (
            <li
              key={a}
              className="flex items-center gap-2 rounded-full border border-line bg-brand-soft px-3 py-1 text-sm"
            >
              <span>{a}</span>
              {isAdmin ? (
                <button
                  type="button"
                  aria-label={`Remove alias ${a}`}
                  onClick={() => remove.mutate(a)}
                  className="text-fail"
                >
                  ×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted">No aliases yet.</p>
      )}
      <form onSubmit={onSubmit} noValidate className="flex items-end gap-2">
        <div className="flex-1">
          <Field
            id="ingredient-add-alias"
            label="Add an alias"
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            placeholder="e.g. scallion"
          />
        </div>
        <Button type="submit" variant="secondary" disabled={add.isPending} block={false}>
          Add
        </Button>
      </form>
      {add.isError ? <Alert tone="error">{messageForError(add.error)}</Alert> : null}
      {remove.isError ? <Alert tone="error">{messageForError(remove.error)}</Alert> : null}
    </div>
  );
}
